<script>
	import { createEventDispatcher, onMount, tick } from "svelte";
	import { parsePhoneNumberWithError, ParseError } from "libphonenumber-js/max";
	import {
		normalizeTelInput,
		getCountryForPartialE164Number,
		generatePlaceholder,
		allowedCharacters
	} from "../../utils/helpers.js";
	import { telInputAction } from "../../utils/telInputAction.js";
	import { projectSettings } from "../../../../config/index";
	import { validator } from "../../../../validation";

	const dispatch = createEventDispatcher();
	const defaultOptions = {
		autoPlaceholder: true,
		spaces: true,
		invalidateOnCountryChange: false,
		format: "national",
		strictCountry: false
	};

	export let autocomplete = null;
	let classes = "";
	export { classes as class };
	export let disabled = false;
	export let id =
		"phone-input-" + /* @__PURE__ */ new Date().getTime().toString(36) + Math.random().toString(36).slice(2);
	export let name = null;
	export let placeholder = null;
	export let readonly = null;
	export let required = null;
	export let size = null;
	export let phone;
	export let country = void 0;
	export let detailedValue = null;
	export let valid = true;
	export let options = defaultOptions;
	export let el = void 0;
	export let validatorMessage = null;
	export let validatorTarget = null;

	let inputValue = phone.PhoneNumber;
	let prevCountry = country;
	let isValidating = false;
	let retryCount = 0;
	let retryLimit = 2;
	let lastValidatedNumber = null;

	const authorization = `Basic ${projectSettings.loqateCredentials}`;

	const combinedOptions = {
		...defaultOptions,
		...options
	};

	const handleInputAction = (value2) => {
		if (disabled || readonly) return;

		handleParsePhoneNumber(value2, country);
	};

	const updateCountry = (countryCode) => {
		if (countryCode !== country) {
			country = countryCode;
			prevCountry = country;
			dispatch("updateCountry", country);
			dispatch("updateDetailedValue", detailedValue);
		}
		return country;
	};

	const findNewCursorPosition = (newValue, formattedValue, initialCursorPosition) => {
		let fvIndex = 0;
		for (let nvIndex = 0; nvIndex < initialCursorPosition; nvIndex++) {
			const nvChar = allowedCharacters(newValue[nvIndex], { spaces: false });
			if (nvChar >= "0" && nvChar <= "9") {
				while (
					!(formattedValue[fvIndex] >= "0" && formattedValue[fvIndex] <= "9") &&
					fvIndex < formattedValue.length
				) {
					fvIndex++;
				}
				fvIndex++;
			}
		}
		return fvIndex;
	};

	async function validatePhone(number, countryCode) {
		if (!number) {
			return true;
		}

		const url = `https://hosted.mastersoftgroup.com/harmony/rest/${countryCode}/validate/phone`;
		const data = {
			payload: [{ fullPhone: number, country: countryCode }]
		};

		const options = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: authorization
			},
			body: JSON.stringify(data)
		};

		try {
			const response = await fetch(url, options);
			const responseData = await response.json();
			const { payload } = responseData;

			const isValid = !payload[0].exception;

			if (isValid) {
				phone.ThirdPartyValidated = true;
				phone.ValidationVendor = "Loqate";
			}
			retryCount = 0;

			return { success: isValid };
		} catch {
			if (retryCount++ >= retryLimit) {
				return { timeout: true };
			} else {
				return validatePhone(number, countryCode);
			}
		}
	}

	export const handleParsePhoneNumber = async (rawInput, currCountry = null, event = null) => {
		if (event && !event.isTrusted) return;

		phone.ThirdPartyValidated = false;
		phone.ValidationVendor = null;
		$validator.clearVisualError(el); // Clear existing error message

		const input = rawInput;

		if (!!input) {
			isValidating = true;

			dispatch("validating", { isValidating });

			const detectedCountry = getCountryForPartialE164Number(input);
			const useCountry = options?.strictCountry ? currCountry : (detectedCountry ?? currCountry);

			if (!options?.strictCountry && detectedCountry && detectedCountry !== prevCountry) {
				updateCountry(detectedCountry);
			}

			try {
				detailedValue = normalizeTelInput(parsePhoneNumberWithError(input, useCountry ?? void 0));
			} catch (err) {
				if (err instanceof ParseError) {
					detailedValue = {
						isValid: false,
						error: err.message
					};
					dispatch("parseError", err.message);
				} else {
					throw err;
				}
			}

			const formatOption = combinedOptions.format === "national" ? "nationalNumber" : "e164";
			const formattedValue = combinedOptions.format === "national" ? "formatOriginal" : "formatInternational";
			const initialCursorPosition = el?.selectionStart || 0;

			if (combinedOptions.spaces && detailedValue?.[formattedValue]) {
				inputValue = detailedValue[formattedValue] ?? null;
				await tick();
				if (el) {
					const newCursorPosition = findNewCursorPosition(input, inputValue, initialCursorPosition);
					el.selectionStart = newCursorPosition;
					el.selectionEnd = newCursorPosition;
				}
			} else if (detailedValue?.[formatOption]) {
				inputValue = detailedValue[formatOption] ?? null;
				await tick();
				if (el) {
					const newCursorPosition = findNewCursorPosition(input, inputValue, initialCursorPosition);
					el.selectionStart = newCursorPosition;
					el.selectionEnd = newCursorPosition;
				}
			}

			phone.PhoneNumber = detailedValue?.e164 ?? input ?? null;
			valid = detailedValue?.isValid ?? false;

			if (valid && !phone.IsLandline && phone.PhoneNumber !== lastValidatedNumber) {
				lastValidatedNumber = phone.PhoneNumber;
				let status = await validatePhone(phone.PhoneNumber, detailedValue.countryCode);
				valid = status.success || status.timeout;
				detailedValue.isValid = valid;
			}

			dispatch("updateValid", valid);
			dispatch("updateValue", phone.PhoneNumber);
			dispatch("updateDetailedValue", detailedValue);

			isValidating = false;
			dispatch("validating", { isValidating });
		} else if (input === null && currCountry !== null) {
			if (currCountry !== prevCountry) {
				prevCountry = currCountry;
				valid = !options.invalidateOnCountryChange;
				phone.PhoneNumber = null;
				inputValue = null;
				detailedValue = null;
				dispatch("updateValid", valid);
				dispatch("updateValue", phone.PhoneNumber);
				dispatch("updateDetailedValue", detailedValue);
			}
		} else {
			valid = true;
			phone.PhoneNumber = null;
			detailedValue = null;
			prevCountry = currCountry;
			dispatch("updateValid", valid);
			dispatch("updateDetailedValue", detailedValue);
			inputValue = null;
		}

		await tick();
		el.dispatchEvent(new Event("change", { bubbles: true }));
	};

	let countryWatchInitRun = true;
	const countryChangeWatchFunction = (current) => {
		if (!countryWatchInitRun) {
			handleParsePhoneNumber(null, current);
		}
		countryWatchInitRun = false;
	};

	$: countryChangeWatchFunction(country);
	$: getPlaceholder =
		combinedOptions.autoPlaceholder && country
			? generatePlaceholder(country, {
					format: combinedOptions.format,
					spaces: combinedOptions.spaces
				})
			: placeholder;

	$: if (phone.PhoneNumber === null && inputValue !== null && detailedValue !== null) {
		inputValue = null;
		detailedValue = null;
		dispatch("updateDetailedValue", detailedValue);
	}

	export const updateValue = (newValue, newCountry) => {
		const castedValue = newValue;
		if (castedValue) {
			handleParsePhoneNumber(
				castedValue,
				options?.strictCountry ? country : getCountryForPartialE164Number(castedValue) || newCountry
			);
		}
	};

	onMount(() => {
		if (phone.PhoneNumber && !phone.ThirdPartyValidated) {
			handleParsePhoneNumber(
				phone.PhoneNumber,
				options?.strictCountry ? country : getCountryForPartialE164Number(phone.PhoneNumber) || country
			);
		}
	});
</script>

<input
	{...$$restProps}
	bind:this={el}
	{autocomplete}
	class={classes}
	{disabled}
	{id}
	{name}
	{readonly}
	{required}
	{size}
	placeholder={getPlaceholder}
	type="tel"
	value={inputValue}
	telinput
	inputmode="numeric"
	on:beforeinput
	on:blur
	on:change
	on:click
	on:focus
	on:input
	on:keydown
	on:keypress
	on:keyup
	on:paste
	data-bouncer-message={validatorMessage}
	data-bouncer-target={validatorTarget}
	data-isvalid={detailedValue ? detailedValue.isValid : true}
	use:telInputAction={{
		handler: handleInputAction,
		spaces: combinedOptions.spaces,
		value: phone.PhoneNumber,
		strictCountryCode: combinedOptions.strictCountry
	}} />

<style>
	input {
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
		height: 50px;
		border: 1px solid var(--color-border);
	}
</style>
